/**
 * Gnosis Chain polling scanner (BRIEF §8, §27).
 *
 * On each tick: load lastScannedBlock, getLogs for ERC20 Transfers touching
 * the watched Safe (both directions) over the new range, classify each, hand
 * card spends to the callback, persist the new watermark. Chunked to respect
 * public-RPC getLogs limits; rotates RPC endpoints on failure.
 */
import {
  createPublicClient,
  http,
  parseAbiItem,
  type PublicClient,
} from "viem";
import { gnosis } from "viem/chains";
import { GNOSIS_CHAIN, GNOSIS_SPEND_TOKENS } from "../config";
import { getScanState, saveScanState } from "../db";
import type { DetectedSpend, Hex } from "../types";
import { classifyTransferLog } from "./classifier";

export interface ScanResult {
  spends: DetectedSpend[];
  scannedTo: number;
}

let rpcIndex = 0;

export function gnosisClient(): PublicClient {
  const url = GNOSIS_CHAIN.rpcUrls[rpcIndex % GNOSIS_CHAIN.rpcUrls.length];
  return createPublicClient({ chain: gnosis, transport: http(url, { timeout: 15_000 }) });
}

export function rotateRpc(): void {
  rpcIndex += 1;
}

/** Retry an RPC operation across every configured endpoint before failing. */
export async function withRpcFailover<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < GNOSIS_CHAIN.rpcUrls.length; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      rotateRpc();
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError ?? "All Gnosis RPCs failed"));
}

const txMetaCache = new Map<string, { txTo: Hex | null; txFrom: Hex; timestamp: number }>();

async function txMeta(client: PublicClient, txHash: Hex) {
  const cached = txMetaCache.get(txHash);
  if (cached) return cached;
  const tx = await client.getTransaction({ hash: txHash });
  const block = await client.getBlock({ blockNumber: tx.blockNumber });
  const meta = {
    txTo: (tx.to ?? null) as Hex | null,
    txFrom: tx.from as Hex,
    timestamp: Number(block.timestamp) * 1000,
  };
  txMetaCache.set(txHash, meta);
  return meta;
}

/**
 * Scan from the persisted watermark to the current head.
 * Returns newly detected card spends (deduplication happens in the pipeline).
 *
 * `onSpend` is invoked for each spend BEFORE the chunk's watermark is
 * persisted: processing is idempotent (keyed on txHash:logIndex), so a crash
 * re-scans a chunk harmlessly — whereas advancing the watermark past
 * unprocessed spends would lose those round-ups forever.
 */
export async function scanForCardSpends(
  safeAddress: Hex,
  onSpend?: (spend: DetectedSpend) => Promise<void>,
): Promise<ScanResult> {
  return withRpcFailover(() => scanWithClient(gnosisClient(), safeAddress, onSpend));
}

async function scanWithClient(
  client: PublicClient,
  safeAddress: Hex,
  onSpend?: (spend: DetectedSpend) => Promise<void>,
): Promise<ScanResult> {
  const head = Number(await client.getBlockNumber());

  const state = await getScanState(safeAddress);
  // First run: start from ~1 day back rather than genesis.
  const dayBlocks = Math.floor(86_400 / GNOSIS_CHAIN.blockTimeSeconds);
  let from = state?.lastScannedBlock ? state.lastScannedBlock + 1 : Math.max(0, head - dayBlocks);
  if (from > head) return { spends: [], scannedTo: state?.lastScannedBlock ?? head };

  const transferEvent = parseAbiItem(
    "event Transfer(address indexed from, address indexed to, uint256 value)",
  );
  // Scoping to the spend-token contracts keeps queries cheap on public RPCs
  // (and mirrors gnosispay/ui's own fetchErc20Transfers approach).
  const tokenAddresses = GNOSIS_SPEND_TOKENS.map((t) => t.address);
  const spends: DetectedSpend[] = [];

  while (from <= head) {
    const to = Math.min(from + GNOSIS_CHAIN.maxLogRangeBlocks - 1, head);
    // Two queries: Safe as sender, Safe as recipient.
    const [outLogs, inLogs] = await Promise.all([
      client.getLogs({
        address: tokenAddresses,
        fromBlock: BigInt(from),
        toBlock: BigInt(to),
        event: transferEvent,
        args: { from: safeAddress },
      }),
      client.getLogs({
        address: tokenAddresses,
        fromBlock: BigInt(from),
        toBlock: BigInt(to),
        event: transferEvent,
        args: { to: safeAddress },
      }),
    ]);

    for (const log of [...outLogs, ...inLogs]) {
      if (log.logIndex == null || log.transactionHash == null || log.blockNumber == null) continue;
      const meta = await txMeta(client, log.transactionHash as Hex);
      const cls = classifyTransferLog(
        {
          address: log.address as Hex,
          topics: log.topics as readonly Hex[],
          data: log.data as Hex,
          transactionHash: log.transactionHash as Hex,
          logIndex: log.logIndex,
          blockNumber: log.blockNumber,
        },
        safeAddress,
        { txTo: meta.txTo, txFrom: meta.txFrom },
      );
      if (cls.kind === "not_card_spend") continue;

      const decimals = cls.token.decimals;
      // Token units → pence, floor. (GBPe/EURe are 1:1 with their fiat.)
      const amountPence = Number((cls.amountUnits * 100n) / 10n ** BigInt(decimals));
      const spend: DetectedSpend = {
        txHash: log.transactionHash as Hex,
        logIndex: log.logIndex,
        blockNumber: Number(log.blockNumber),
        timestamp: meta.timestamp,
        merchant: cls.kind === "refund" ? "Card refund" : "Card purchase",
        amountPence,
        currency: cls.token.currency,
        kind: cls.kind === "refund" ? "refund" : "card_spend",
      };
      if (onSpend) await onSpend(spend);
      spends.push(spend);
    }

    from = to + 1;
    await saveScanState({ safeAddress, lastScannedBlock: to });
  }

  return { spends, scannedTo: head };
}
